import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Area, AreaChart,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Sparkles, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PALETTES = {
  verdictMix: {
    'Interested':            '#10b981',
    'Not Interested':        '#ef4444',
    'Partially Interested':  '#f59e0b',
    'Other Concerns':        '#a855f7',
  },
  notInterested: {
    'Non-Conversation':       '#f43f5e',
    'Commercial Negotiation': '#f97316',
    'Wrong Contact':          '#eab308',
    'No Market Demand':       '#64748b',
    'Locked with Competitor': '#78716c',
    'Prefers Other Brands':   '#9ca3af',
  },
  interested: {
    'Enquiry Call':             '#10b981',
    'Payment Process':          '#059669',
    'Requested Order List':     '#14b8a6',
    'Asked to Share Catalogue': '#06b6d4',
    'Price Comparison':         '#0ea5e9',
    'Instock':                  '#22d3ee',
    'Offline Website Customer': '#67e8f9',
  },
};

const VIEWS = [
  { id: 'verdictMix',     label: 'Verdict Mix',             sub: 'All 4 tiers week over week' },
  { id: 'notInterested',  label: 'Not-Interested Reasons',  sub: '6 reasons by week' },
  { id: 'interested',     label: 'Interested Signals',      sub: 'Buying intent categories' },
];

const RANGES = [
  { id: 4,  label: '4w' },
  { id: 8,  label: '8w' },
  { id: 12, label: '12w' },
];

// Tier filter emitted when a chart point is clicked, based on current view.
const VIEW_TIER = {
  verdictMix: null, // tier is dynamic — set to the series name clicked
  notInterested: 'Not Interested',
  interested: 'Interested',
};

function wowDelta(data, key) {
  if (!data || data.length < 2) return 0;
  const last = data[data.length - 1][key] || 0;
  const prev = data[data.length - 2][key] || 0;
  if (prev === 0) return last === 0 ? 0 : 100;
  return Math.round(((last - prev) / prev) * 100);
}

function trendDirection(data, key) {
  if (!data || data.length < 4) return 'flat';
  const half = Math.floor(data.length / 2);
  const earlier = data.slice(0, half).reduce((s, d) => s + (d[key] || 0), 0);
  const latest  = data.slice(half).reduce((s, d) => s + (d[key] || 0), 0);
  const diff = latest - earlier;
  if (Math.abs(diff) < Math.max(2, earlier * 0.1)) return 'flat';
  return diff > 0 ? 'up' : 'down';
}

const DirectionIcon = ({ dir }) => {
  if (dir === 'up')   return <TrendingUp   className="w-3.5 h-3.5" />;
  if (dir === 'down') return <TrendingDown className="w-3.5 h-3.5" />;
  return <Minus className="w-3.5 h-3.5" />;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  const weekRange = row.week_start && row.week_end
    ? `${row.week_start} → ${row.week_end}`
    : null;
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-3 py-2 text-xs max-w-[260px]">
      <div className="font-semibold text-slate-700">{label}</div>
      {weekRange && <div className="text-[10px] text-slate-400 mb-1.5">{weekRange}</div>}
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-600 flex-1 truncate">{p.dataKey}</span>
          <span className="font-semibold text-slate-800 tabular-nums">{p.value}</span>
        </div>
      ))}
      <div className="text-[10px] text-slate-400 mt-1.5 pt-1.5 border-t border-slate-100">
        Click a point to open Call Logs for this week
      </div>
    </div>
  );
};

export default function TrendInsights({ agentId, dataSource }) {
  const navigate = useNavigate();
  const [view, setView] = useState('verdictMix');
  const [range, setRange] = useState(8);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrends = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('weeks', String(range));
      if (agentId) params.set('agent_id', agentId);
      if (dataSource) params.set('data_source', dataSource);
      const res = await axios.get(`${API}/dashboard/trends?${params.toString()}`);
      setPayload(res.data);
    } catch (e) {
      setError(e?.message || 'Failed to load trends');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [range, agentId, dataSource]);

  useEffect(() => { fetchTrends(); }, [fetchTrends]);

  const seriesKeys = payload?.[view]?.series || [];
  const data       = payload?.[view]?.data   || [];
  const palette    = PALETTES[view];

  const movers = useMemo(() => {
    return seriesKeys
      .map((k) => ({ name: k, delta: wowDelta(data, k), dir: trendDirection(data, k) }))
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 3);
  }, [data, seriesKeys]);

  const isVerdictMix = view === 'verdictMix';
  const ChartEl = isVerdictMix ? AreaChart : LineChart;

  const totalCalls = useMemo(() => {
    return data.reduce((acc, row) => {
      return acc + seriesKeys.reduce((s, k) => s + (row[k] || 0), 0);
    }, 0);
  }, [data, seriesKeys]);

  // Build a Call Logs URL for a single week × series click.
  const openWeekInCallLogs = useCallback((row, seriesName) => {
    if (!row || !seriesName) return;
    const params = new URLSearchParams();
    params.set('dateFrom', row.week_start);
    params.set('dateTo', row.week_end);
    if (view === 'verdictMix') {
      params.set('tier', seriesName);
    } else if (view === 'notInterested') {
      params.set('tier', 'Not Interested');
      params.set('qcCategory', seriesName);
    } else if (view === 'interested') {
      params.set('tier', 'Interested');
      params.set('qcCategory', seriesName);
    }
    navigate(`/call-logs?${params.toString()}`);
  }, [view, navigate]);

  const handleChartClick = useCallback((e) => {
    if (!e || !e.activePayload || !e.activePayload.length) return;
    // When clicking a point, open the series under that point with the
    // highest value in that week (most meaningful).
    const row = e.activePayload[0].payload;
    const topSeries = seriesKeys
      .map((k) => ({ k, v: row[k] || 0 }))
      .sort((a, b) => b.v - a.v)[0];
    if (topSeries && topSeries.v > 0) {
      openWeekInCallLogs(row, topSeries.k);
    }
  }, [seriesKeys, openWeekInCallLogs]);

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      data-testid="trend-insights-card"
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Trend Insights</h2>
            <p className="text-sm text-slate-500">
              {VIEWS.find(v => v.id === view)?.sub} · Last {range} weeks
              {payload?.range && (
                <span className="ml-2 text-[11px] text-slate-400">
                  ({payload.range.from} → {payload.range.to})
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Range toggle */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {RANGES.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRange(r.id)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                range === r.id
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`trend-range-${r.id}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* View tabs */}
      <div className="px-5 pb-3 flex gap-2 border-b border-slate-100">
        {VIEWS.map(v => (
          <button
            key={v.id}
            type="button"
            onClick={() => setView(v.id)}
            className={`relative px-3 py-2 text-sm font-medium transition-colors ${
              view === v.id ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`trend-view-${v.id}`}
          >
            {v.label}
            {view === v.id && (
              <span className="absolute left-3 right-3 -bottom-[1px] h-[2px] bg-gradient-to-r from-indigo-500 to-violet-600 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Mover chips */}
      {!loading && !error && data.length > 0 && (
        <div className="px-5 pt-4 flex items-center gap-2 flex-wrap min-h-[32px]">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Biggest WoW movers
          </span>
          {movers.length === 0 || movers.every(m => m.delta === 0) ? (
            <span className="text-xs text-slate-400 italic">No week-over-week changes yet</span>
          ) : (
            movers.map(m => {
              const positive = m.delta > 0;
              const neutral = m.delta === 0;
              const chipColor = neutral
                ? 'bg-slate-50 text-slate-500 border-slate-200'
                : positive
                  ? (view === 'notInterested' ? 'bg-rose-50 text-rose-700 border-rose-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100')
                  : (view === 'notInterested' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100');
              return (
                <div
                  key={m.name}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${chipColor}`}
                  data-testid={`trend-mover-${m.name}`}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: palette[m.name] }}
                  />
                  <span>{m.name}</span>
                  <DirectionIcon dir={m.dir} />
                  <span className="tabular-nums">
                    {m.delta > 0 ? '+' : ''}{m.delta}%
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Chart body */}
      <div className="px-2 pt-3 pb-4" style={{ height: 320 }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-400">
            Loading trend data…
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center text-sm text-rose-500 gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <button
              type="button"
              onClick={fetchTrends}
              className="text-xs text-indigo-600 hover:text-indigo-700 underline"
            >
              Retry
            </button>
          </div>
        ) : totalCalls === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-400">
            No calls in the selected range.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ChartEl
              data={data}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <defs>
                {seriesKeys.map(k => (
                  <linearGradient key={k} id={`grad-${view}-${k}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={palette[k]} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={palette[k]} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="week"
                stroke="#94a3b8"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis
                stroke="#94a3b8"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={32}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
              {seriesKeys.map((k) =>
                isVerdictMix ? (
                  <Area
                    key={k}
                    type="monotone"
                    dataKey={k}
                    stroke={palette[k]}
                    strokeWidth={2}
                    fill={`url(#grad-${view}-${k})`}
                    activeDot={{ r: 5, cursor: 'pointer' }}
                  />
                ) : (
                  <Line
                    key={k}
                    type="monotone"
                    dataKey={k}
                    stroke={palette[k]}
                    strokeWidth={2}
                    dot={{ r: 2.5, fill: palette[k], strokeWidth: 0 }}
                    activeDot={{ r: 5, cursor: 'pointer' }}
                  />
                )
              )}
            </ChartEl>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
